import type { LLPStatusEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type LLPRow = LLPStatusEvent & { pageId?: string };

export function LLPTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: LLPRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: LLPRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>Part #</Th>
          <Th>S/N</Th>
          <Th>Name</Th>
          <Th>IIN</Th>
          <Th>Assy</Th>
          <Th>Date</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>Variants</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="font-mono">{r.partNumber || '—'}</Td>
            <Td className="font-mono">{r.serialNumber || '—'}</Td>
            <TruncatedTd value={r.name} maxWidth="max-w-[240px]" />
            <Td className="font-mono">{r.iin ?? '—'}</Td>
            <Td>{r.assy ?? '—'}</Td>
            <Td>{r.date ?? '—'}</Td>
            <Td>{r.tsn ?? '—'}</Td>
            <Td>{r.csn ?? '—'}</Td>
            {/* engineVariants can be absent when the agentic variants group times out */}
            <TruncatedTd value={formatEngineVariants(r.engineVariants)} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** One `thrust · CSN n · LL n · R n` clause per variant, joined with ` / `. */
function formatEngineVariants(variants: LLPRow['engineVariants']): string | undefined {
  if (!variants?.length) return undefined;
  return variants
    .map((v) =>
      [
        v.thrust,
        v.csn != null ? `CSN ${v.csn}` : undefined,
        v.lifeLimitCycles != null ? `LL ${v.lifeLimitCycles}` : undefined,
        v.remainingCycles != null ? `R ${v.remainingCycles}` : undefined,
      ]
        .filter(Boolean)
        .join(' · '),
    )
    .join(' / ');
}
